import { doc, getDoc, type Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { createApiCall } from '../../../services/apiClient';
import { OnboardingProgress, type ObsessLessUser } from '../../../types/commonTypes';
import type { GenerateOcdCycleStepsResponse, OcdCycleStepsData } from './ocdCycleStepsTypes';

/**
 * Above the API's long AI budget, so a slow regeneration is not cut off client-side. The
 * server-side ceiling is Cloud Run's 300s request timeout; the run is idempotent, so a
 * retry after a timeout costs a read rather than another generation.
 */
const REGENERATE_TIMEOUT_MS = 540_000;

const regenerateOcdCycleStepsCall = createApiCall<{ userId: string }, GenerateOcdCycleStepsResponse>(
  'adminGenerateOcdCycleSteps',
  { timeoutMs: REGENERATE_TIMEOUT_MS }
);

// A read only: it does not write an audit entry, so it cannot raise the account-change alert.
const getAuthUserCall = createApiCall<{ userId: string }, { displayName: string | null }>('adminGetCustomClaims');

export class UserProfileService {
  private static instance: UserProfileService;

  static getInstance(): UserProfileService {
    if (!UserProfileService.instance) {
      UserProfileService.instance = new UserProfileService();
    }
    return UserProfileService.instance;
  }

  async getUserProfile(userId: string): Promise<ObsessLessUser | null> {
    const userRef = doc(db, 'user_info', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data();
    if (!userData) {
      return null;
    }

    // Convert Firestore Timestamps to Date objects
    const result: ObsessLessUser = {
      uid: userId,
      email: userData.email ?? null,
      displayName: userData.displayName ?? null,
      createdAt: (userData.createdAt as Timestamp)?.toDate(),
      hasFinishedOnboarding: userData.hasFinishedOnboarding ?? false,
      onboardingProgress: userData.onboardingProgress ?? '',
      referralCode: userData.referralCode,
      // Normalized rather than passed through: the flags are missing on every user who has
      // never had one set, and the toggles should render off for them rather than undefined.
      featureFlags: { voiceMode: userData.featureFlags?.voiceMode === true },
      timezone: userData.timezone,
    };

    if (userData.deviceInfo) {
      result.deviceInfo = {
        ...userData.deviceInfo,
        lastSeenAt: (userData.deviceInfo.lastSeenAt as Timestamp | undefined)?.toDate(),
      };
    }

    // Convert questJourney timestamps if present
    if (userData.questJourney) {
      result.questJourney = {
        createdAt: (userData.questJourney.createdAt as Timestamp)?.toDate(),
        currentDay: userData.questJourney.currentDay,
        isCurrentDayCompleted: userData.questJourney.isCurrentDayCompleted,
        journeyDayStartedAt: (userData.questJourney.journeyDayStartedAt as Timestamp)?.toDate(),
        updatedAt: (userData.questJourney.updatedAt as Timestamp)?.toDate(),
      };

      if (userData.questJourney.journeyDayCompletedAt) {
        result.questJourney.journeyDayCompletedAt = (userData.questJourney.journeyDayCompletedAt as Timestamp)?.toDate();
      }
    }

    if (userData.sttUsage) {
      result.sttUsage = {
        sessionsThisWeek: userData.sttUsage.sessionsThisWeek ?? 0,
        weeklyResetTimestamp: (userData.sttUsage.weeklyResetTimestamp as Timestamp)?.toDate(),
      };
    }

    if (userData.voiceUsage) {
      result.voiceUsage = {
        conversationsThisWeek: userData.voiceUsage.conversationsThisWeek ?? 0,
        minutesThisWeek: userData.voiceUsage.minutesThisWeek ?? 0,
        weeklyResetTimestamp: (userData.voiceUsage.weeklyResetTimestamp as Timestamp)?.toDate(),
      };
    }

    return result;
  }

  async updateOnboardingStatus(userId: string, onboardingProgress: string): Promise<void> {
    const userRef = doc(db, 'user_info', userId);

    // Check if document exists first
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const hasFinishedOnboarding = onboardingProgress === OnboardingProgress.FinishedOnboarding;

    await updateDoc(userRef, {
      onboardingProgress: onboardingProgress,
      hasFinishedOnboarding: hasFinishedOnboarding,
    });
  }

  async getOcdCycleSteps(userId: string): Promise<OcdCycleStepsData | null> {
    const docRef = doc(db, `user_info/${userId}/metadata/ocd_cycle_steps`);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    const steps = data.steps as OcdCycleStepsData['steps'];

    if (!steps || steps.length === 0) {
      return null;
    }

    return {
      steps,
      createdAt: (data.createdAt as Timestamp | undefined)?.toDate(),
      updatedAt: (data.updatedAt as Timestamp | undefined)?.toDate(),
    };
  }

  async regenerateOcdCycleSteps(userId: string): Promise<GenerateOcdCycleStepsResponse> {
    return regenerateOcdCycleStepsCall({ userId });
  }

  /**
   * The name on the Firebase Auth user. It can differ from `user_info.displayName`: the app's
   * name change writes only the profile document.
   */
  async getAuthDisplayName(userId: string): Promise<string | null> {
    const { displayName } = await getAuthUserCall({ userId });
    return displayName;
  }

  async updateSttUsage(userId: string, sessionsThisWeek: number): Promise<void> {
    const userRef = doc(db, 'user_info', userId);

    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const existingData = userDoc.data();
    await updateDoc(userRef, {
      'sttUsage.sessionsThisWeek': sessionsThisWeek,
      ...(existingData?.sttUsage?.weeklyResetTimestamp
        ? {}
        : { 'sttUsage.weeklyResetTimestamp': new Date() }),
    });
  }

  /**
   * Turns voice mode on or off for one user. The dotted path creates `featureFlags` on the
   * first write, so there is nothing to backfill on users who have never had a flag.
   */
  async updateVoiceModeEnabled(userId: string, enabled: boolean): Promise<void> {
    const userRef = doc(db, 'user_info', userId);

    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    await updateDoc(userRef, { 'featureFlags.voiceMode': enabled });
  }

  async updateVoiceUsage(
    userId: string,
    conversationsThisWeek: number,
    minutesThisWeek: number
  ): Promise<void> {
    const userRef = doc(db, 'user_info', userId);

    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const existingData = userDoc.data();
    await updateDoc(userRef, {
      'voiceUsage.conversationsThisWeek': conversationsThisWeek,
      'voiceUsage.minutesThisWeek': minutesThisWeek,
      ...(existingData?.voiceUsage?.weeklyResetTimestamp
        ? {}
        : { 'voiceUsage.weeklyResetTimestamp': new Date() }),
    });
  }
}

export const userProfileService = UserProfileService.getInstance();
